/**
 * Human labels for the lower_snake step codes a UI may branch on (docs/api-tenant-ops.md §4.1, §4.6, §10).
 * Each label is a short explanation of what happened and what to do; the code itself is always shown next to
 * it, verbatim.
 */
export const STEP_REASON_LABELS: Record<string, string> = {
  unlimited_plan: 'Unlimited AI plan — there is nothing to grant',
  kb_not_ready: 'Waiting for the knowledge base to be ingested before the pack agents deploy',
  kb_not_ready_timeout: 'Gave up waiting for the knowledge base — retry once the documents are ready',
  agents_pack_refused: 'The agents service refused part of the pack — waiting cannot fix it',
  optional_section_refused: 'The service refused an optional pack section',
  endpoint_absent:
    "The service's image predates this step's endpoint, so nothing was applied — upgrade the service, then retry the step",
  tenant_db_ready_publish_failed:
    'IAM created the tenant but could not publish TenantDbReady — services self-heal via registry scans within 60 s; retry re-publishes',
  iam_step_failed: 'IAM created the tenant but a non-fatal IAM sub-step failed — retry re-runs the idempotent create',
};

/**
 * Codes that turn a finished job into `done_with_warnings`. A warning can sit on a SKIPPED step (the pack and
 * endpoint cases) or on a DONE one (`iam.create`: the tenant exists, a non-fatal IAM sub-step failed).
 */
export const WARNING_CODES = new Set([
  'kb_not_ready_timeout',
  'agents_pack_refused',
  'optional_section_refused',
  'endpoint_absent',
  'tenant_db_ready_publish_failed',
  'iam_step_failed',
]);

export function stepReasonLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return STEP_REASON_LABELS[code] ?? null;
}

/**
 * A deadline relative to now: `in 43 min`, `in 2 h 05 min`, `due now`, `12 min ago`. Paired with the
 * absolute time on screen — a countdown alone goes stale in a screenshot.
 */
export function formatUntil(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return iso;
  const minutes = Math.round((at - now) / 60_000);
  const abs = Math.abs(minutes);
  const text = abs >= 60 ? `${Math.floor(abs / 60)} h ${String(abs % 60).padStart(2, '0')} min` : `${abs} min`;
  if (minutes === 0) return 'due now';
  return minutes > 0 ? `in ${text}` : `${text} ago`;
}
